/**
 * Planning Layout — shell for Planning Mode (Doc 17).
 *
 * Sidebar with product-mode switcher (Prioritization | Planning) and Planning nav.
 * Sets RLS workspace context when tenant context is available (Migration 059).
 */

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, LogOut } from 'lucide-react';
import { PlanningSidebar } from './PlanningSidebar';
import { clearTenantContext } from '../../lib/tenantContext';
import { useWorkspaceContextBootstrap } from '../../hooks/useWorkspaceContextBootstrap';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { usePlanningDefaultsStore } from '../../stores/prioritization/planningDefaultsStore';
import { useResourceTypesStore } from '../../stores/prioritization/resourceTypesStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { ThemeToggle } from '../ui/ThemeToggle';
import { NotificationBell } from '../prioritization/NotificationBell';
import { SyncStatusIndicator } from '../prioritization/SyncStatusIndicator';
import { PUBLIC_DOCS_SITE_URL } from '../../lib/publicDocsSite';
import { useUserStore } from '../../stores/prioritization/userStore';
import { ToastProvider } from '../ui/Toast';
import { PlanInstrumentNavProvider } from '../../contexts/PlanInstrumentNavContext';
import { PLANNING_PLAN_INSTRUMENT_PATH } from '../../lib/prioritizationPlanInstrumentPath';
import { setSettingsEntryProduct } from '../../lib/productSurfacePaths';
// ── Mobile experience (isolated, feature-flagged, fully rollback-safe) ──
// Rollback: flip localStorage pa_mobile_ui="off", or revert these two lines +
// the early return below, then delete src/mobile/PlanningMobileShell.tsx and
// src/mobile/planningNavConfig.ts.  Desktop (>= 768px) is never affected.
import { useMobileExperience } from '../../mobile/featureFlag';
import { PlanningMobileShell } from '../../mobile/PlanningMobileShell';
import { monoIconButton } from '@/components/mono/iconButton';

interface PlanningLayoutProps {
  children: ReactNode;
}

export default function PlanningLayout({ children }: PlanningLayoutProps) {
  const navigate = useNavigate();
  const signOut = useUserStore((s) => s.signOut);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);

  // Back on a real Plans route — clear the Settings entry marker so a later
  // Priorities→Settings visit does not keep Plans chrome from a prior visit.
  useEffect(() => {
    setSettingsEntryProduct(null);
  }, []);

  // Load DB-backed planning stores when workspace is known
  useEffect(() => {
    if (!currentWorkspaceId) return;
    void usePlanningDefaultsStore.getState().load(currentWorkspaceId);
    void useResourceTypesStore.getState().load(currentWorkspaceId);
  }, [currentWorkspaceId]);

  // The same workspace bootstrap every product shell runs: a direct load or hard refresh of
  // any /planning/* URL initializes the tenant context and opens SpaceSwitcher's gates just
  // as the Priorities layout does. Plans also sets the Postgres RLS workspace (Migration 059).
  useWorkspaceContextBootstrap({
    onContext: (ctx) => {
      getSupabaseClient()
        .rpc('set_workspace_context', { wid: ctx.workspaceId })
        .then(() => {})
        .catch(() => {});
    },
  });

  // useMobileExperience must be called unconditionally (hook rules).
  // On desktop (>= 768px) it returns false and everything below is unchanged.
  const useMobileShell = useMobileExperience();

  if (useMobileShell) {
    // Wrap in PlanInstrumentNavProvider so plan-instrument links inside pages work
    // correctly on mobile (same context as desktop).
    return (
      <PlanInstrumentNavProvider basePath={PLANNING_PLAN_INSTRUMENT_PATH}>
        <PlanningMobileShell>{children}</PlanningMobileShell>
      </PlanInstrumentNavProvider>
    );
  }

  return (
    <ToastProvider>
      <PlanInstrumentNavProvider basePath={PLANNING_PLAN_INSTRUMENT_PATH}>
        <div className="min-h-screen bg-[var(--bg-raised)] dark:bg-[var(--bg)] transition-colors duration-200">
          <PlanningSidebar />
          <div
            className={`flex flex-col min-h-screen transition-[padding] duration-200 ${
              sidebarOpen ? 'pl-64' : 'pl-16'
            }`}
          >
            {/* Match PrioritizationLayout top chrome height (h-14) so switching Priorities ↔ Plans doesn't jump */}
            <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-b border-[var(--rule)] dark:border-[var(--rule)] h-14 px-6 flex-shrink-0 flex items-center justify-end gap-1.5">
              <button
                type="button"
                className={monoIconButton('sm')}
                title="Search (⌘K)"
                aria-label="Open command palette (⌘K)"
              >
                <Search className="h-4 w-4 shrink-0" />
              </button>
              <a
                href={PUBLIC_DOCS_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={monoIconButton('sm')}
                title="Documentation"
                aria-label="Documentation"
              >
                <BookOpen className="h-4 w-4 shrink-0" />
              </a>
              <ThemeToggle variant="icon" />
              <NotificationBell />
              <SyncStatusIndicator />
              <button
                type="button"
                onClick={() => {
                  clearTenantContext();
                  signOut();
                  navigate('/auth/login', { replace: true });
                }}
                className={monoIconButton('sm')}
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4 shrink-0" />
              </button>
            </div>
            <main className="flex-1 pt-[22px] pb-6 px-6">{children}</main>
          </div>
        </div>
      </PlanInstrumentNavProvider>
    </ToastProvider>
  );
}
